/**
 * Which set of books a report is about (docs/design/currency.md R6, CUR-2).
 *
 * A trial balance or a financial statement is one set of books, in one
 * functional currency. In a workspace whose legal entities keep their books
 * in different currencies the API refuses to add them together and answers
 * 422 LEGAL_ENTITY_REQUIRED, listing the entities. The page then asks which
 * one, from that list, and asks the API again with `legalEntityId`.
 */
import { useEffect, useState } from "react";
import { Field, Select } from "./ui";
import { ApiError } from "@/lib/api/client";
import { useTranslation } from "@/lib/i18n";

export interface LegalEntityChoice {
  id: string;
  name: string;
  currencyCode: string;
}

/**
 * The entities a 422 LEGAL_ENTITY_REQUIRED offers; null for any other
 * outcome, so the caller shows that error as an error.
 */
export function legalEntitiesRequired(
  error: unknown,
): LegalEntityChoice[] | null {
  if (!(error instanceof ApiError) || error.code !== "LEGAL_ENTITY_REQUIRED")
    return null;
  const listed = error.extensions.legalEntities;
  if (!Array.isArray(listed)) return [];
  return listed.flatMap((row: unknown) => {
    const r = (row ?? {}) as Record<string, unknown>;
    return typeof r.id === "string" &&
      typeof r.name === "string" &&
      typeof r.currencyCode === "string"
      ? [{ id: r.id, name: r.name, currencyCode: r.currencyCode }]
      : [];
  });
}

/**
 * The legal entities to choose from: those the refusal lists, remembered once
 * it has gone, so the choice can still be changed after the report loads.
 */
export function useOfferedLegalEntities(error: unknown): LegalEntityChoice[] {
  const [remembered, setRemembered] = useState<LegalEntityChoice[]>([]);
  const offered = legalEntitiesRequired(error);
  const current = offered && offered.length > 0 ? offered : null;
  useEffect(() => {
    const listed = legalEntitiesRequired(error);
    if (listed && listed.length > 0) setRemembered(listed);
  }, [error]);
  return current ?? remembered;
}

/**
 * The query's retry rule: a refusal that asks for a legal entity is an
 * answer, not a failure, so it is shown at once rather than asked again
 * (which waits for the window's focus when it is not focused). Anything else
 * is retried once, as the app's default.
 */
export function retryUnlessLegalEntityRequired(
  failureCount: number,
  error: unknown,
): boolean {
  return legalEntitiesRequired(error) === null && failureCount < 1;
}

/** `path` with `legalEntityId` added when one is chosen. */
export function withLegalEntity(path: string, legalEntityId: string): string {
  if (!legalEntityId) return path;
  const join = path.includes("?") ? "&" : "?";
  return `${path}${join}legalEntityId=${encodeURIComponent(legalEntityId)}`;
}

export function LegalEntityPicker({
  entities,
  value,
  onChange,
}: {
  entities: readonly LegalEntityChoice[];
  value: string;
  onChange: (id: string) => void;
}) {
  const { t } = useTranslation();
  if (entities.length === 0) return null;
  return (
    <div className="mb-4">
      {!value && (
        <p role="status" style={{ marginBlockStart: 0 }}>
          {t("books.legalEntityRequired")}
        </p>
      )}
      <Field label={t("books.legalEntity")} required>
        <Select
          value={value}
          required
          onChange={(e) => onChange(e.target.value)}
        >
          <option value="">{t("books.chooseLegalEntity")}</option>
          {entities.map((entity) => (
            <option key={entity.id} value={entity.id}>
              {t("books.entityOption", {
                name: entity.name,
                currency: entity.currencyCode,
              })}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}
